import RBush from 'rbush';
import type { Store } from '@annotorious/core';
import { createNanoEvents, type Unsubscribe } from 'nanoevents';
import type { AnnotationRects } from '../state/text-annotation-store';
import { mergeClientRects, getHighlightClientRects, toParentBounds } from '../utils/highlight';
import type {
  RevivedTextAnnotationLike,
  RevivedTextAnnotationTargetLike,
  TextAnnotationLike,
  TextAnnotationTargetLike
} from '../model';

interface IndexedHighlightRect {

  minX: number;

  minY: number;

  maxX: number;

  maxY: number;

  annotation: {
    
    id: string;

    rects: DOMRect[];

  }

}

export interface SpatialTreeEvents {

  recalculate(): void;

}

export const createSpatialTree = <T extends TextAnnotationLike>(
  store: Store<T>,
  container: HTMLElement,
  hMergeTolerance?: number,
  vMergeTolerance?: number
) => {

  const tree = new RBush<IndexedHighlightRect>();

  const index = new Map<string, IndexedHighlightRect[]>();

  const emitter = createNanoEvents<SpatialTreeEvents>();

  // Helper: converts a single text annotation target to a list of hightlight rects
  const toItems = (target: RevivedTextAnnotationTargetLike, offset: DOMRect): IndexedHighlightRect[] => {
    const rects = target.selector.flatMap(s => getHighlightClientRects(s.range));

    /**
     * Offset the merged client rects so that coords
     * are relative to the parent container
     */
    const merged = mergeClientRects(rects, hMergeTolerance, vMergeTolerance)
      .map(rect => toParentBounds(rect, offset));

    return merged.map(rect => {
      const { x, y, width, height } = rect;

      return {
        minX: x,
        minY: y,
        maxX: x + width,
        maxY: y + height,
        annotation: {
          id: target.annotation,
          rects: merged
        }
      }
    });
  }

  const all = () => [...index.values()];

  const clear = () => {
    tree.clear();
    index.clear();
  }

  const insert = (target: RevivedTextAnnotationTargetLike) => {
    const rects = toItems(target, container.getBoundingClientRect());
    if (rects.length === 0) return;

    rects.forEach(rect => tree.insert(rect));
    index.set(target.annotation, rects);
  }

  const remove = (target: TextAnnotationTargetLike) => {
    const rects = index.get(target.annotation);
    if (rects) {
      rects.forEach(rect => tree.remove(rect));
      index.delete(target.annotation);
    }
  }

  const update = (target: RevivedTextAnnotationTargetLike) => {
    remove(target);
    insert(target);
  }

  const set = (targets: RevivedTextAnnotationTargetLike[], replace: boolean = true) => {
    if (replace)
      clear();

    const offset = container.getBoundingClientRect();

    const rectsByTarget = targets.map(target => ({ target, rects: toItems(target, offset) }));
    rectsByTarget.forEach(({ target, rects }) => {
      if (rects.length > 0)
        index.set(target.annotation, rects)
    });

    const allRects = rectsByTarget.flatMap(({ rects }) => rects);
    tree.load(allRects);
  }

  const getAt = (x: number, y: number, all = false): string[] => {
    const hits = tree.search({
      minX: x,
      minY: y,
      maxX: x,
      maxY: y
    });

    const area = (rect: IndexedHighlightRect) =>
      rect.annotation.rects.reduce((area, r) =>
        area + r.width * r.height, 0);
    
    // Get smallest rect
    if (hits.length > 0) {
      hits.sort((a, b) => area(a) - area(b));
      return all ? hits.map(h => h.annotation.id) : [ hits[0].annotation.id ];
    } else {
      return [];
    }
  }

  const getAnnotationBounds = (id: string): DOMRect | undefined => {
    const rects = getAnnotationRects(id);

    if (rects.length === 0)
      return;

    let left = rects[0].left;
    let top = rects[0].top;
    let right = rects[0].right;
    let bottom = rects[0].bottom;

    for (let i = 1; i < rects.length; i++) {
      const rect = rects[i];

      left = Math.min(left, rect.left);
      top = Math.min(top, rect.top);
      right = Math.max(right, rect.right);
      bottom = Math.max(bottom, rect.bottom);
    }

    return new DOMRect(left, top, right - left, bottom - top);
  }

  const getAnnotationRects = (id: string): DOMRect[] => {
    const indexed = index.get(id);
    if (indexed) {
      // Reminder: *each* IndexedHighlightRect stores *all*
      // DOMRects for the annotation for convenience
      return indexed[0].annotation.rects;
    } else {
      return [];
    }
  }

  const getIntersecting = (
    minX: number, 
    minY: number, 
    maxX: number, 
    maxY: number,
  ): AnnotationRects<RevivedTextAnnotationLike<T>>[] => {
    // All rects in this area, regardless of annotation
    const rects = tree.search({ minX, minY, maxX, maxY });

    // Distinct annotation IDs
    const annotationIds = new Set(rects.map(rect => rect.annotation.id));

    // Resolve annotation IDs. Note that the tree could be slightly out of sync (because 
    // it updates by listening to changes, just like anyone else)
    return Array.from(annotationIds).map(annotationId => ({
      annotation: store.getAnnotation(annotationId) as RevivedTextAnnotationLike<T>,
      rects: getAnnotationRects(annotationId)
    })).filter(t => Boolean(t.annotation));
  }

  const size = () => tree.all().length;

  const recalculate = () => {
    set(store.all().map(a => a.target as RevivedTextAnnotationTargetLike), true);
    emitter.emit('recalculate');
  };

  const on = <E extends keyof SpatialTreeEvents>(event: E, callback: SpatialTreeEvents[E]): Unsubscribe => emitter.on(event, callback);

  return {
    all,
    clear,
    getAt,
    getAnnotationBounds,
    getAnnotationRects,
    getIntersecting,
    insert,
    recalculate,
    remove,
    set,
    size,
    update,
    on
  }

}
